/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { When } from "react-if";
import { Spacer, Heading, theme, Text, Label } from "@nordcloud/gnui";
import { FormGroup, TransformableInput } from "~/components";
import { isNotEmpty } from "~/tools";
import { StandardForm } from "~/views/accounts/components/StandardForm";
import { AccountContacts, AccountParams } from "~/views/accounts/types";
import { StepContainer } from "../components/StepContainer";
import { StyledListItem, BoldText } from "../components/styles";
import {
  URL_IBMCLOUD,
  URL_IBMCLOUD_ACCOUNT_OVERVIEW,
  URL_IBMCLOUD_IAM_USER,
  URL_IBMCLOUD_NOTICATION,
  URL_IBMCLOUD_CREATE_API_KEY,
} from "../constants";
import { trimTransform } from "../utils";
import { schemaCredsIbmcloud } from "../validators";

type Props = {
  onAddAccountSubmit: SubmitHandler<FormData>;
  id: string;
  accountParams: AccountParams;
  additionalSettings: AccountContacts;
  setAdditionalSettings: (settings: AccountContacts) => void;
};

enum FormField {
  OWNER_ID = "ownerId",
  CONTACT_IDS = "contactIds",
  PROVIDER_ID = "providerId",
  APP_KEY = "appKey",
}

export function IbmcloudAccountForm({
  onAddAccountSubmit,
  accountParams,
  additionalSettings,
  setAdditionalSettings,
  ...props
}: Props) {
  const formMethods = useForm({
    resolver: yupResolver(schemaCredsIbmcloud),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = formMethods;

  React.useEffect(() => {
    register(FormField.OWNER_ID);
    register(FormField.CONTACT_IDS);
  }, [register]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onAddAccountSubmit)} {...props}>
        <Spacer height="2rem" />
        <StandardForm
          accountParams={accountParams}
          additionalSettings={additionalSettings}
          setAdditionalSettings={setAdditionalSettings}
        />

        <When condition={isNotEmpty(accountParams.provider)}>
          <Spacer height="1rem" />
          <Heading level={3} marginBottom="spacing03">
            Account Credentials
          </Heading>
          <Text color={theme.color.text.text02}>
            To onboard IBM, you must set up an IBM user account for your main
            account as proxy account. The main account is the account with
            resources, and provides you with user access. You use the proxy
            account to create the API Key you need to carry out the scanning of
            resources.
          </Text>
        </When>

        <StepContainer counter={1}>
          <Text>
            Sign in to{" "}
            <a href={URL_IBMCLOUD} target="_blank" rel="noreferrer">
              IBM Cloud Platform
            </a>{" "}
            , with the credentials of the main account.
          </Text>
        </StepContainer>
        <StepContainer counter={2}>
          <Text>
            On the{" "}
            <a
              href={URL_IBMCLOUD_ACCOUNT_OVERVIEW}
              target="_blank"
              rel="noreferrer"
            >
              Account Overview
            </a>{" "}
            page, find and record the Account ID from the{" "}
            <BoldText>My user details</BoldText> tab.
          </Text>
          <FormGroup error={errors[FormField.PROVIDER_ID]}>
            <Label
              required
              htmlFor={FormField.PROVIDER_ID}
              name="Provide Main Account ID"
            />
            <TransformableInput
              name={FormField.PROVIDER_ID}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              defaultValue={accountParams.providerId}
              placeholder="e.g. 89642a64558a41a9b19577d4456gc123"
              id={FormField.PROVIDER_ID}
            />
          </FormGroup>
        </StepContainer>
        <StepContainer counter={3}>
          <Text>
            On the{" "}
            <a href={URL_IBMCLOUD_IAM_USER} target="_blank" rel="noreferrer">
              Access(IAM) Users
            </a>{" "}
            page, invite the proxy account.
          </Text>
        </StepContainer>
        <StepContainer counter={4}>
          <Text>
            In a separate browser, sign in to{" "}
            <a href={URL_IBMCLOUD} target="_blank" rel="noreferrer">
              IBM Cloud Platform{" "}
            </a>
            with proxy account. Then on the{" "}
            <a href={URL_IBMCLOUD_NOTICATION} target="_blank" rel="noreferrer">
              IBM Cloud Notication
            </a>{" "}
            page, accept the invitation you sent from the main account.
          </Text>
        </StepContainer>
        <StepContainer counter={5}>
          <Text>
            On the Users page of the main account, grant{" "}
            <BoldText>Reader</BoldText> access and <BoldText>Viewer</BoldText>{" "}
            access, to the proxy account in main account page
          </Text>
          <StyledListItem>
            Select <BoldText>Manage</BoldText>, then{" "}
            <BoldText>Access(IAM)</BoldText>, and then click{" "}
            <BoldText>User</BoldText> from the left menu. Select{" "}
            <BoldText>proxy account</BoldText>, then click{" "}
            <BoldText>Access policies</BoldText> on the top tab, and then click{" "}
            <BoldText>Assign access</BoldText>
          </StyledListItem>
          <StyledListItem>
            On Assign access page, select <BoldText>IAM services</BoldText>,
            then select{" "}
            <BoldText>All Identity and Access enabled services</BoldText>. For{" "}
            <BoldText>How do you want to scope the access?</BoldText>, select{" "}
            <BoldText>All resources</BoldText>. Then select{" "}
            <BoldText>Viewer </BoldText>and <BoldText>Reader</BoldText> for{" "}
            <BoldText>Platform access</BoldText>,{" "}
            <BoldText>Service access</BoldText>, and{" "}
            <BoldText>Resource group access</BoldText>. Then, click{" "}
            <BoldText>Add</BoldText>.
          </StyledListItem>
          <StyledListItem>
            Select <BoldText>Account Management</BoldText>, then{" "}
            <BoldText>All Account management Service</BoldText>, and then select{" "}
            <BoldText>Viewer</BoldText> access. Then, click{" "}
            <BoldText>Add</BoldText>.
          </StyledListItem>
          <StyledListItem>
            Select <BoldText>Cloud foundry</BoldText>, then select current
            organization, and then select <BoldText>Auditor</BoldText> access
            for the Organization role, and for the Space role. Click{" "}
            <BoldText>Add</BoldText>, then click <BoldText>Assign</BoldText> on
            the right.
          </StyledListItem>
          <StyledListItem>
            Select <BoldText>Class infrastructure</BoldText>, then click{" "}
            <BoldText>Manage user infrastructure access page</BoldText>, and
            then select <BoldText>view only</BoldText> in Permission sets. Then,
            click <BoldText>Apply</BoldText> in the Permissions tag. Then,
            select all options in <BoldText>Select type</BoldText>, and select{" "}
            <BoldText>Enable future access</BoldText> in the Devices tag.
          </StyledListItem>
          As a result, on the <BoldText>Access Policies</BoldText> tab, you now
          see multiple <BoldText>Viewer</BoldText> roles.
        </StepContainer>
        <StepContainer counter={6}>
          <Text>
            Switch to the page for the proxy account, and create the API Key.
            For more information, see:{" "}
            <a
              href={URL_IBMCLOUD_CREATE_API_KEY}
              target="_blank"
              rel="noreferrer"
            >
              API Keys document
            </a>
            .
          </Text>
        </StepContainer>
        <StepContainer counter={7}>
          <FormGroup error={errors[FormField.APP_KEY]}>
            <Label
              required
              htmlFor={FormField.APP_KEY}
              name="Provide the API Key from the proxy account"
            />
            <TransformableInput
              name={FormField.APP_KEY}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              placeholder="e.g XEogykgnehnuMKZUEM-abFxjK7uCNF9FOdBRI9-KgTMR"
              id={FormField.APP_KEY}
            />
          </FormGroup>
        </StepContainer>
      </form>
    </FormProvider>
  );
}
